import { useEffect, useRef, useCallback } from 'react';
import { useToast } from '../components/Toast';
import { getDevUserId } from '../lib/api';
import type { ConnectionState, Message, Channel, PendingMessage } from '../types';
import { useAuthStore } from '../stores/useAuthStore';
import { useChannelStore } from '../stores/useChannelStore';
import { useChannelMemberStore } from '../stores/useChannelMemberStore';
import { useMessageStore } from '../stores/useMessageStore';
import { useDmStore } from '../stores/useDmStore';
import { usePresenceStore } from '../stores/usePresenceStore';
import {
  onMessageReceived,
  onMessageAcked,
  heldMessagesAtGapEnd,
  resyncAfterReconnect,
} from '../stores/actions';
import { useTypingStore } from '../stores/useTypingStore';
import { useThreadStore } from '../stores/useThreadStore';
import { useTaskStore } from '../stores/useTaskStore';
import { useServerStore } from '../stores/useServerStore';
import { useAgentActivityStore } from '../stores/useAgentActivityStore';
import { useTaskRunStore } from '../stores/useTaskRunStore';
import {
  dispatchInvitationPending,
  dispatchInvitationDecided,
  dispatchMentionPushed,
} from './useWsHubFrames';
import type {
  AgentInvitationPendingFrame,
  AgentInvitationDecidedFrame,
  MentionPushedFrame,
  AgentActivityFrame,
  AgentTaskStateChangedFrame,
} from '../types/ws-frames';
import { markPresence } from './usePresence';
import { markRT3Presence } from './useRT3Presence';
import { useActivityHeartbeat } from './useActivityHeartbeat';
import type { AgentRuntimeReason, AgentRuntimeState } from '../lib/api';
import { reduceRealtimeFrame } from '../client-core/realtime';
import { notifyExecutionUsageUpdated } from '../lib/execution-usage-updates';

const PING_INTERVAL = 25_000;
const DEFAULT_RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const E2E_RECONNECT_DELAY_RAW = import.meta.env.VITE_E2E_WS_RECONNECT_DELAY_MS;
const E2E_RECONNECT_DELAY = Number(E2E_RECONNECT_DELAY_RAW);
const RECONNECT_DELAYS =
  import.meta.env.DEV &&
  E2E_RECONNECT_DELAY_RAW !== undefined &&
  E2E_RECONNECT_DELAY_RAW !== '' &&
  Number.isFinite(E2E_RECONNECT_DELAY) &&
  E2E_RECONNECT_DELAY >= 0
    ? [E2E_RECONNECT_DELAY]
    : DEFAULT_RECONNECT_DELAYS;
const AUTH_FAILURE_CODES = new Set([4001, 4003]);

// How long the reconnect repair waits before each further attempt. The schedule
// is short and finite: it exists to ride out a REST rail that is briefly still
// refusing reads on a socket that is already back, not to keep a permanently
// broken repair running.
const RESYNC_RETRY_DELAYS = [1000, 3000, 9000];

/**
 * bf task ws-auth-gate (fix-skill-findings): the SPA must NOT open `/ws`
 * before the session cookie is set, or the server rejects with 401 and the
 * reconnect loop spams the browser console (borgee-local-e2e skill first-run
 * counted ~11 entries during a fresh signup). The `isAuthenticated` argument
 * gates connect: while false the hook is inert (no `new WebSocket(...)`); on
 * transition to true the effect re-runs and `connect()` fires once. The
 * argument is optional and defaults to `true` so legacy call sites (and the
 * existing test harnesses that don't model auth) keep the previous behavior.
 *
 * Lock: useWebSocket-auth-gate.test.tsx asserts the wire-level contract
 * (zero `new WebSocket()` while `isAuthenticated === false`, exactly one on
 * transition to true).
 */
export function useWebSocket(isAuthenticated = true, onAuthClose?: () => void) {
  useActivityHeartbeat();
  const { showToast } = useToast();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>();
  const pingTimer = useRef<ReturnType<typeof setInterval>>();
  const subscribedChannels = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  const scheduleReconnectRef = useRef<() => void>();
  const ackTimers = useRef<Map<string, () => void>>(new Map());
  const handleMessageRef = useRef<(data: { type: string; [key: string]: unknown }) => void>(
    () => {},
  );
  const onAuthCloseRef = useRef(onAuthClose);
  onAuthCloseRef.current = onAuthClose;

  // Use store selectors for reactive values
  const connectionState = usePresenceStore((s) => s.connectionState);
  const dmChannels = useDmStore((s) => s.dmChannels);
  const pendingMessages = useMessageStore((s) => s.pendingMessages);

  const currentChannelIdRef = useRef(useChannelStore.getState().currentChannelId);
  currentChannelIdRef.current = useChannelStore.getState().currentChannelId;

  const findPendingChannelId = useCallback(
    (clientMessageId: string): string | null => {
      const pendingMessages = useMessageStore.getState().pendingMessages;
      for (const [channelId, pending] of Object.entries(pendingMessages)) {
        if (pending.some((p) => p.clientMessageId === clientMessageId)) return channelId;
      }
      return null;
    },
    [pendingMessages],
  );

  const setConnectionState = useCallback((cs: ConnectionState) => {
    // Lifecycle frames are ephemeral. Never keep a pulse through a socket gap;
    // cards rehydrate from the durable agent status snapshot on reconnect.
    if (cs !== 'connected') useTaskRunStore.getState().clear();
    usePresenceStore.getState().setConnectionState(cs);
  }, []);

  /**
   * Resolves the optimistic rows whose sends the gap swallowed the ack for.
   *
   * The rows offered here are only what the re-read newly learned, never the
   * whole window it returned: a send is matched by sender and text, so a message
   * the user had already posted with the same text — and which the store already
   * held — would otherwise claim a pending row that never left the browser, and
   * the retry affordance would vanish with it.
   */
  const reconcilePendingMessages = useCallback(
    (channelId: string, learnedMessages: Message[]) => {
      const pending = useMessageStore.getState().pendingMessages[channelId];
      if (!pending || pending.length === 0) return;
      const learnedContents = new Set(learnedMessages.map((m) => `${m.sender_id}:${m.content}`));
      for (const p of pending) {
        if (learnedContents.has(`${p.senderId}:${p.content}`)) {
          useMessageStore.getState().removePendingMessage(p.clientMessageId, channelId);
          ackTimers.current.get(p.clientMessageId)?.();
          ackTimers.current.delete(p.clientMessageId);
        }
      }
    },
    [pendingMessages],
  );

  const reconcilePendingRef = useRef(reconcilePendingMessages);
  reconcilePendingRef.current = reconcilePendingMessages;

  /**
   * Rebuilds the state the gap cost, and keeps trying while the socket holds.
   *
   * An open socket says nothing about the REST rail being reachable — a server
   * that has just come back answers the socket before it answers reads, and a
   * proxy in front of it can be answering 502 either way — while a repair that
   * gives up on the first refusal leaves the tab quietly wrong until the next
   * disconnect, which may never come. Once the socket is no longer this one, its
   * successor's own repair owns the state and this one stands down.
   */
  const repairAfterReconnect = useCallback(async (ws: WebSocket) => {
    const heldAtGapEnd = heldMessagesAtGapEnd();
    for (let attempt = 0; ; attempt++) {
      try {
        const { staleSubscriptions, learnedMessages } = await resyncAfterReconnect(
          subscribedChannels.current,
          heldAtGapEnd,
        );
        for (const channelId of staleSubscriptions) {
          subscribedChannels.current.delete(channelId);
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
          }
        }
        for (const [channelId, messages] of Object.entries(learnedMessages)) {
          reconcilePendingRef.current(channelId, messages);
        }
        return;
      } catch (err) {
        const delay = RESYNC_RETRY_DELAYS[attempt];
        if (delay === undefined) {
          console.warn('[ws] reconnect repair failed, giving up:', err);
          return;
        }
        console.warn('[ws] reconnect repair failed, retrying in %dms:', delay, err);
        await new Promise((resolve) => setTimeout(resolve, delay));
        if (!mountedRef.current || ws.readyState !== WebSocket.OPEN) return;
      }
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current) {
      const rs = wsRef.current.readyState;
      if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) return;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const params = new URLSearchParams();
    const userId = getDevUserId();
    if (userId) params.set('user_id', userId);
    const url = `${protocol}//${host}/ws${params.toString() ? `?${params}` : ''}`;

    setConnectionState(reconnectAttempt.current > 0 ? 'reconnecting' : 'connecting');

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      const wasReconnect = reconnectAttempt.current > 0;
      reconnectAttempt.current = 0;
      setConnectionState('connected');

      const selfId = useAuthStore.getState().currentUser?.id;
      if (selfId) {
        usePresenceStore.getState().userOnline(selfId);
        markRT3Presence(selfId, 'online', undefined);
      }

      // Re-subscribe to all channels
      for (const channelId of subscribedChannels.current) {
        ws.send(JSON.stringify({ type: 'subscribe', channel_id: channelId }));
      }

      // A gap in the socket is a gap in the truth: the missed pushes are gone,
      // so the state they would have carried is re-read from the tables that
      // hold it rather than replayed.
      if (wasReconnect) {
        void repairAfterReconnect(ws);
      }

      // Start heartbeat
      if (pingTimer.current) clearInterval(pingTimer.current);
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, PING_INTERVAL);
    };

    ws.onmessage = (event) => {
      if (!mountedRef.current) return;
      try {
        const frame = JSON.parse(event.data);
        handleMessageRef.current(frame as { type: string; [key: string]: unknown });
      } catch {
        // Invalid JSON
      }
    };

    ws.onclose = (event) => {
      if (!mountedRef.current) return;
      cleanup();

      if (AUTH_FAILURE_CODES.has(event.code)) {
        setConnectionState('disconnected');
        console.warn('[ws] Auth failure (code %d), not reconnecting:', event.code, event.reason);
        onAuthCloseRef.current?.();
        return;
      }

      console.info('[ws] Closed (code %d), scheduling reconnect', event.code);
      scheduleReconnectRef.current?.();
    };

    ws.onerror = () => {
      // onclose will fire after onerror
    };
  }, [setConnectionState, repairAfterReconnect]);

  const cleanup = useCallback(() => {
    if (pingTimer.current) {
      clearInterval(pingTimer.current);
      pingTimer.current = undefined;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    setConnectionState('reconnecting');
    const delay =
      RECONNECT_DELAYS[Math.min(reconnectAttempt.current, RECONNECT_DELAYS.length - 1)]!;
    reconnectAttempt.current++;
    reconnectTimer.current = setTimeout(() => {
      if (mountedRef.current) connect();
    }, delay);
  }, [connect, setConnectionState]);

  scheduleReconnectRef.current = scheduleReconnect;

  const handleMessage = useCallback(
    (data: { type: string; [key: string]: unknown }) => {
      const coreEffects = reduceRealtimeFrame(data);
      switch (data.type) {
        case 'new_message': {
          const message = coreEffects.find((effect) => effect.kind === 'message.received')
            ?.message as Message | undefined;
          if (!message || !subscribedChannels.current.has(message.channel_id)) break;
          onMessageReceived(message.channel_id, message);
          break;
        }
        case 'thread_create': {
          // A cached collection needs the whole lifecycle, not just updates. A
          // thread born after loadThreads() took its snapshot would otherwise
          // never enter the store, and every later count update for it would
          // land on nothing.
          const thread = data.thread as
            | (Channel & {
                parent_id?: string;
                message_id?: string;
                message_count?: number;
              })
            | undefined;
          if (
            thread?.id &&
            thread.parent_id &&
            thread.message_id &&
            subscribedChannels.current.has(thread.parent_id)
          ) {
            useThreadStore.getState().upsertThread({
              id: thread.id,
              message_id: thread.message_id,
              parent_channel_id: thread.parent_id,
              message_count: thread.message_count ?? 0,
            });
          }
          break;
        }
        case 'thread_updated': {
          // The frame identifies the thread completely, so a count for a thread
          // whose creation event was missed still renders instead of being
          // dropped — the same call serves both cases.
          const threadId = data.thread_id as string;
          const parentChannelId = data.channel_id as string | undefined;
          const originMessageId = data.message_id as string | undefined;
          if (
            threadId &&
            parentChannelId &&
            originMessageId &&
            subscribedChannels.current.has(parentChannelId)
          ) {
            useThreadStore.getState().upsertThread({
              id: threadId,
              message_id: originMessageId,
              parent_channel_id: parentChannelId,
              message_count: data.message_count as number,
            });
          }
          break;
        }
        case 'task_updated':
        case 'task_create': {
          const task = data.task as any;
          const activeGuildId = useAuthStore.getState().guildId;
          const knownChannel = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === task?.channel_id && channel.guild_id === activeGuildId,
            );
          if (
            task?.id &&
            task.guild_id === activeGuildId &&
            knownChannel &&
            subscribedChannels.current.has(task.channel_id)
          ) {
            useTaskStore.getState().upsertTask(task);
          }
          break;
        }
        case 'task_deleted': {
          const taskId = data.task_id as string;
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const knownChannel = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (
            taskId &&
            data.guild_id === activeGuildId &&
            knownChannel &&
            subscribedChannels.current.has(channelId)
          ) {
            useTaskStore.getState().removeTask(taskId);
          }
          break;
        }
        case 'agent_task_state_changed': {
          const frame = data as unknown as AgentTaskStateChangedFrame;
          if (frame.task_id) {
            // The hub already scoped delivery to the subscribed task channel.
            useTaskRunStore.getState().setTaskRun(frame.task_id, frame.agent_id, frame.state === 'busy');
          }
          break;
        }
        case 'presence': {
          const userId = data.user_id as string;
          const status = data.status as string;
          if (status === 'online') {
            usePresenceStore.getState().userOnline(userId);
          } else {
            usePresenceStore.getState().userOffline(userId);
          }
          // Server emits the 'presence' frame for every WS user (humans + agents
          // share /ws). The agent presence cache read by usePresence() only fills
          // from 'presence.changed' frames, and the server currently does not emit
          // those, so without this mirror agents show "已离线" even while their
          // runtime is connected. Humans currently have no usePresence(humanId)
          // consumers, so writing them into the cache is a harmless no-op. Only
          // online/offline come through this path;
          // error / busy / idle remain on the 'presence.changed' frame.
          if (status === 'online' || status === 'offline') {
            markPresence(userId, status, undefined);
            // Feed the human multi-device presence store so member rows reflect
            // real presence instead of being permanently offline. The server
            // broadcasts this `presence` frame for every WS user on connect /
            // disconnect (server-go internal/ws/client.go), so online/offline here
            // is the in-scope data source. The four-state away/thinking fanout is
            // not emitted today; away is derived from last-seen inside
            // useRT3Presence, and thinking has no server frame yet.
            markRT3Presence(userId, status, undefined);
          }
          break;
        }
        // Agent runtime presence carries {agent_id, status, reason?}. Do not
        // accept IP / last_heartbeat_at / connection_count; the server strips
        // them. 5s throttling is enforced on server egress and again at the
        // client markPresence entry point.
        // NB: busy/idle is NOT part of this frame; AL-1b ships those on the
        // separate 'agent_task_state_changed' frame (see server
        // packages/server-go/internal/ws/agent_task_state_changed_frame.go).
        case 'presence.changed': {
          const agentID = data.agent_id as string | undefined;
          const status = data.status as AgentRuntimeState | undefined;
          const reason = data.reason as AgentRuntimeReason | undefined;
          if (agentID && (status === 'online' || status === 'offline' || status === 'error')) {
            markPresence(agentID, status, reason);
          }
          break;
        }
        case 'guilds_changed': {
          void useServerStore
            .getState()
            .refreshGuilds()
            .catch(() => {});
          break;
        }
        case 'guild_removed': {
          const guildId = data.guild_id as string | undefined;
          if (guildId && guildId === useAuthStore.getState().guildId) {
            window.dispatchEvent(
              new CustomEvent('guild_membership_removed', { detail: { guildId } }),
            );
          }
          void useServerStore
            .getState()
            .refreshGuilds()
            .catch(() => {});
          break;
        }
        case 'guild_invites_changed': {
          window.dispatchEvent(new CustomEvent('guild_invites_changed', { detail: data }));
          break;
        }
        case 'channel_created':
        case 'channel_added': {
          const channel = data.channel as Channel | undefined;
          if (channel?.id && channel.guild_id === useAuthStore.getState().guildId) {
            useChannelStore.getState().addChannel(channel);
          }
          break;
        }
        case 'channel_removed':
        case 'channel_deleted': {
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (!channelId || data.guild_id !== activeGuildId || !known) break;
          useChannelStore.getState().removeChannel(channelId);
          subscribedChannels.current.delete(channelId);
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
          }
          if (data.type === 'channel_deleted' && currentChannelIdRef.current === channelId) {
            const name = data.name as string | undefined;
            showToast(name ? `频道 #${name} 已被删除` : '频道已被删除');
          }
          break;
        }
        case 'channel_move_source_evict': {
          const sourceGuildId = data.source_guild_id as string | undefined;
          if (!sourceGuildId || sourceGuildId !== useAuthStore.getState().guildId) break;
          const subtree = Array.isArray(data.subtree_channel_ids)
            ? data.subtree_channel_ids.filter((id): id is string => typeof id === 'string')
            : [];
          if (subtree.length === 0) break;
          for (const channelId of subtree) {
            subscribedChannels.current.delete(channelId);
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
            }
            useTypingStore.getState().clearChannel(channelId);
            useChannelMemberStore.getState().clearChannel(channelId);
            useAgentActivityStore.getState().clearChannel(channelId);
          }
          useChannelStore.getState().purgeSubtree(subtree);
          useMessageStore.getState().purgeChannels(subtree);
          useThreadStore.getState().purgeChannels(subtree);
          useTaskStore.getState().purgeChannels(subtree);
          break;
        }
        case 'channel_move_target_refresh': {
          const targetGuildId = data.target_guild_id as string | undefined;
          if (targetGuildId && targetGuildId === useAuthStore.getState().guildId) {
            void useChannelStore
              .getState()
              .loadChannels(targetGuildId)
              .catch(() => {});
            void useTaskStore.getState().loadGuildTasks(targetGuildId);
          }
          break;
        }
        case 'visibility_changed': {
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (data.guild_id === activeGuildId && known) {
            useChannelStore.getState().updateChannel(channelId, {
              visibility: data.visibility as 'public' | 'private',
            });
          }
          break;
        }
        case 'channel_updated': {
          const channel = data.channel as Channel | undefined;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (candidate) => candidate.id === channel?.id && candidate.guild_id === activeGuildId,
            );
          if (channel?.id && channel.guild_id === activeGuildId && known) {
            useChannelStore.getState().updateChannel(channel.id, channel);
          }
          break;
        }
        case 'channels_reordered': {
          const channelId = data.channel_id as string;
          const activeGuildId = useAuthStore.getState().guildId;
          const known = useChannelStore
            .getState()
            .channels.some(
              (channel) => channel.id === channelId && channel.guild_id === activeGuildId,
            );
          if (data.guild_id !== activeGuildId || !known) break;
          const updates: Partial<Channel> = {};
          if (typeof data.position === 'string') updates.position = data.position;
          useChannelStore.getState().updateChannel(channelId, updates);
          break;
        }
        case 'user_joined': {
          const joinedChannelId = data.channel_id as string;
          if (!subscribedChannels.current.has(joinedChannelId)) break;
          const joinedUserId = data.user_id as string;
          if (joinedUserId) {
            usePresenceStore.getState().userOnline(joinedUserId);
          }
          if (joinedChannelId) {
            if (typeof data.member_count === 'number') {
              useChannelStore
                .getState()
                .updateChannel(joinedChannelId, { member_count: data.member_count as number });
            }
            useChannelStore.getState().bumpChannelMembersVersion(joinedChannelId);
          }
          break;
        }
        case 'user_left': {
          const leftChannelId = data.channel_id as string;
          if (!subscribedChannels.current.has(leftChannelId)) break;
          if (leftChannelId) {
            if (typeof data.member_count === 'number') {
              useChannelStore
                .getState()
                .updateChannel(leftChannelId, { member_count: data.member_count as number });
            }
            useChannelStore.getState().bumpChannelMembersVersion(leftChannelId);
          }
          break;
        }
        case 'pong':
          break;
        case 'typing': {
          const channelId = data.channel_id as string;
          if (!subscribedChannels.current.has(channelId)) break;
          const userId = data.user_id as string;
          const displayName = data.display_name as string;
          useTypingStore.getState().setTyping(channelId, userId, displayName);
          break;
        }
        case 'reaction_update': {
          const channelId = data.channel_id as string;
          if (!subscribedChannels.current.has(channelId)) break;
          useMessageStore
            .getState()
            .updateReactions(
              channelId,
              data.message_id as string,
              data.reactions as { emoji: string; count: number; user_ids: string[] }[],
            );
          break;
        }
        case 'message_ack': {
          const effect = coreEffects.find((candidate) => candidate.kind === 'message.acknowledged');
          const clientMessageId =
            effect?.kind === 'message.acknowledged' ? effect.clientMessageId : null;
          const serverMessage =
            effect?.kind === 'message.acknowledged'
              ? (effect.message as unknown as Message)
              : undefined;
          if (clientMessageId && serverMessage) {
            onMessageAcked(serverMessage.channel_id, clientMessageId, serverMessage);

            ackTimers.current.get(clientMessageId)?.();
            ackTimers.current.delete(clientMessageId);
          }
          break;
        }
        case 'message_nack': {
          const nackEffect = coreEffects.find((effect) => effect.kind === 'message.rejected');
          const nackClientId =
            nackEffect?.kind === 'message.rejected' ? nackEffect.clientMessageId : null;
          if (nackClientId) {
            const channelId = findPendingChannelId(nackClientId);
            if (channelId) {
              useMessageStore.getState().failPendingMessage(nackClientId, channelId);
            }
            ackTimers.current.get(nackClientId)?.();
            ackTimers.current.delete(nackClientId);
          }
          break;
        }
        case 'message_sent':
          break;
        case 'message_edited': {
          const editedMsg = data.message as Message;
          if (!subscribedChannels.current.has(editedMsg.channel_id)) break;
          useMessageStore.getState().editMessage(editedMsg.channel_id, editedMsg);
          break;
        }
        case 'message_deleted': {
          const deletedChannelId = data.channel_id as string;
          if (!subscribedChannels.current.has(deletedChannelId)) break;
          const deletedMessageId = data.message_id as string;
          const deletedAt = data.deleted_at as number;
          useMessageStore.getState().deleteMessage(deletedChannelId, deletedMessageId, deletedAt);
          break;
        }
        case 'commands_updated': {
          window.dispatchEvent(new CustomEvent('commands_updated'));
          break;
        }
        case 'agent_invitation_pending': {
          // Bridge the owner-side push to a window CustomEvent so InvitationsInbox /
          // Sidebar can subscribe without coupling to this hook.
          // Schema lock: docs/blueprint/realtime.md §2.3 (exact BPP schema match).
          dispatchInvitationPending(data as unknown as AgentInvitationPendingFrame);
          break;
        }
        case 'agent_invitation_decided': {
          // Synchronize approve/reject/expire across the user's clients.
          dispatchInvitationDecided(data as unknown as AgentInvitationDecidedFrame);
          break;
        }
        case 'mention_pushed': {
          // The frame signals that the current user was mentioned in a message.
          // Its envelope is signal-only;
          // MessageList listens via useMentionPushed and refreshes the
          // channel via actions.loadMessages. body_preview is server-truncated
          // to 80 runes; the client must not re-parse it (server data-trim constraint,
          // full body arrives via the existing message backfill path).
          // mention_pushed_frame.go::MentionPushedFrame defines the exact wire shape.
          dispatchMentionPushed(data as unknown as MentionPushedFrame);
          break;
        }
        case 'execution_usage_updated': {
          const channelId = typeof data.channel_id === 'string' ? data.channel_id : '';
          if (channelId && subscribedChannels.current.has(channelId)) {
            notifyExecutionUsageUpdated(channelId);
          }
          break;
        }
        case 'agent_activity': {
          // The whole payload is the state, so it goes straight into the store
          // rather than waking a pull: nothing durable stands behind it to read.
          const frame = data as unknown as AgentActivityFrame;
          if (subscribedChannels.current.has(frame.channel_id)) {
            useAgentActivityStore.getState().applyFrame(frame);
          }
          break;
        }
        case 'error':
          console.warn('[ws] Server error:', data.message);
          break;
      }
    },
    [showToast, findPendingChannelId],
  );

  handleMessageRef.current = handleMessage;

  const subscribe = useCallback((channelId: string) => {
    if (subscribedChannels.current.has(channelId)) return;
    subscribedChannels.current.add(channelId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'subscribe', channel_id: channelId }));
    }
  }, []);

  const unsubscribe = useCallback((channelId: string) => {
    subscribedChannels.current.delete(channelId);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
    }
  }, []);

  // DMs belong to the account, while every other subscription is authorized
  // by the active Server (including thread channel ids that do not appear in
  // the Server's channel listing). A context switch drops that whole set in
  // one synchronous pass so reconnect cannot resurrect the previous Server.
  const unsubscribeServerChannels = useCallback(() => {
    const dmIds = new Set(useDmStore.getState().dmChannels.map((dm) => dm.id));
    for (const channelId of [...subscribedChannels.current]) {
      if (dmIds.has(channelId)) continue;
      subscribedChannels.current.delete(channelId);
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'unsubscribe', channel_id: channelId }));
      }
    }
  }, []);

  // Connect on mount (gated on isAuthenticated — see hook docblock).
  // While `isAuthenticated` is false the effect body is a no-op, so the
  // pre-auth `/ws` 401 spam observed by borgee-local-e2e cannot happen.
  // The effect re-runs on transition to true and `connect()` fires once.
  // Cleanup still runs on unmount and on the false→true→unmount path.
  useEffect(() => {
    mountedRef.current = true;
    if (!isAuthenticated) {
      // Subscription intent belongs to the authenticated account. Retaining it
      // here would make the next account resubscribe to the previous account's
      // channels before its own bootstrap establishes a context.
      subscribedChannels.current.clear();
      return () => {
        mountedRef.current = false;
      };
    }
    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      cleanup();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isAuthenticated, connect, cleanup]);

  // Auto-subscribe to DM channels
  useEffect(() => {
    if (!isAuthenticated) return;
    for (const dm of dmChannels) {
      if (!subscribedChannels.current.has(dm.id)) {
        subscribe(dm.id);
      }
    }
  }, [isAuthenticated, dmChannels, subscribe]);

  const sendWsMessage = useCallback((payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
    }
  }, []);

  const registerAckTimer = useCallback((clientMessageId: string, cancel: () => void) => {
    ackTimers.current.set(clientMessageId, cancel);
  }, []);

  return {
    subscribe,
    unsubscribe,
    unsubscribeServerChannels,
    sendWsMessage,
    registerAckTimer,
    connectionState,
  };
}
